import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import {
  ClockCircleOutlined,
  DislikeFilled,
  LikeFilled,
  UnorderedListOutlined,
  EllipsisOutlined
} from '@ant-design/icons';
import { Table, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SortOrder, SorterResult } from 'antd/es/table/interface';

import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import { formatDate, parseDate } from 'helpers/date';
import type { IRefund } from 'v2/interfaces/requestManagement/refund';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import refundService from 'v2/services/requestManagement/refund';
import tableService from 'v2/services/table';

const RefundList = () => {
  useObservable(() => refundService.loadRefunds(), []);
  const [isLoading] = useObservable(() => tableService.isLoading(), []);

  const [list] = useObservable(() => refundService.list(), []);
  const [pagination] = useObservable(() => refundService.getPaginationResponse(), []);
  const [filter] = useObservable(() => refundService.getFilter(), []);

  const onCheckAll = useCallback(() => refundService.checkAllRefunds(), []);
  const onUnCheckAll = useCallback(() => refundService.unCheckAllRefunds(), []);
  const onCheck = useCallback((itemId: string) => refundService.checkRefund(itemId), []);

  const hasApprovePermission = eduzzAccountsService.hasPermission('myeduzz:request-management:aprovar');
  const hasDetailsPermission = eduzzAccountsService.hasPermission('myeduzz:request-management:detalhes');

  const buildMenuItems = (refund: IRefund): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (hasDetailsPermission) {
      items.push({
        key: 'details',
        icon: <UnorderedListOutlined />,
        label: 'Detalhes',
        onClick: () => refundService.openDetails(refund)
      });
    }

    if (hasApprovePermission) {
      if (items.length > 0) {
        items.push({ type: 'divider' });
      }

      items.push({
        key: 'approve',
        icon: <LikeFilled style={{ color: '#009358' }} />,
        label: 'Aprovar',
        onClick: () => refundService.openApprove(refund)
      });
      items.push({ type: 'divider' });
      items.push({
        key: 'reprove',
        icon: <DislikeFilled style={{ color: '#f44336' }} />,
        label: 'Reprovar',
        onClick: () => refundService.openReproveMotive(refund)
      });
    }

    return items;
  };

  const sortOrderFor = (field: string): SortOrder => {
    if (filter?.order?.sorty_by !== field) {
      return null;
    }

    return filter.order.order_by === 'asc' ? 'ascend' : 'descend';
  };

  const handleTableChange = (
    tablePagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<IRefund> | SorterResult<IRefund>[]
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (currentSorter?.order) {
      const orderBy = currentSorter.order === 'ascend' ? 'asc' : 'desc';
      const sortBy = String(currentSorter.columnKey);
      const hasChanged = filter?.order?.sorty_by !== sortBy || filter?.order?.order_by !== orderBy;

      if (hasChanged) {
        refundService.setFilter({ order: { sorty_by: sortBy, order_by: orderBy } });
      }
    }

    const { current, pageSize } = tablePagination;

    if (pageSize && pageSize !== pagination?.per_page) {
      refundService.setPagination({ current_page: 1, per_page: pageSize });
      return;
    }

    if (current && current !== pagination?.current_page) {
      refundService.setPagination({ current_page: current, per_page: pagination?.per_page ?? pageSize });
    }
  };

  const columns: ColumnsType<IRefund> = [
    {
      title: 'Pagamento',
      key: 'invoice_payment_date',
      dataIndex: 'invoice_payment_date',
      sorter: true,
      sortOrder: sortOrderFor('invoice_payment_date'),
      render: (value: Date) => formatDate(value, 'dd/MM/yyyy')
    },
    {
      title: 'Comprador',
      key: 'buyer_name',
      dataIndex: 'buyer_name'
    },
    {
      title: 'Fatura',
      key: 'invoice_id',
      dataIndex: 'invoice_id'
    },
    {
      title: 'Valor',
      key: 'invoice_value',
      dataIndex: 'invoice_value',
      sorter: true,
      sortOrder: sortOrderFor('invoice_value'),
      render: (value: number) => <FormattedNumber format='BRL' value={value} />
    },
    {
      title: 'Solicitado dentro do prazo',
      key: 'requested_after_deadline',
      dataIndex: 'requested_after_deadline',
      render: (requestedAfterDeadline: boolean) =>
        requestedAfterDeadline ? (
          <p className='max-w-[45px] rounded-[5px] bg-[#ddd] p-[10px] font-medium text-[#78909c]'>Não</p>
        ) : (
          <p className='text-success max-w-[45px] rounded-[5px] bg-[#d2ebd3] p-[10px] font-medium'>Sim</p>
        )
    },
    {
      title: 'Prazo de resposta',
      key: 'created_at',
      sorter: true,
      sortOrder: sortOrderFor('created_at'),
      render: (_: unknown, refund: IRefund) => {
        const createdAt = parseDate(refund.created_at) ?? new Date(refund.created_at);
        const dateToDisplayWarning = addDays(createdAt, 4);
        const isExpiring = !isBefore(startOfDay(new Date()), startOfDay(dateToDisplayWarning));

        return (
          <StatusTooltip isExpired={isExpiring} isRequestedAfterDeadline={refund.requested_after_deadline}>
            <div className={isExpiring ? 'flex items-center text-red-500' : undefined}>
              {isExpiring && <ClockCircleOutlined style={{ fontSize: 20, marginRight: 4 }} />}
              {formatDate(refund.due_date, 'dd/MM/yyyy')}
            </div>
          </StatusTooltip>
        );
      }
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      width: 60,
      render: (_: unknown, refund: IRefund, index: number) => {
        const menuItems = buildMenuItems(refund);

        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <button className='cursor-pointer border-none bg-transparent'>
              <EllipsisOutlined id={`more-icon-${index}`} style={{ fontSize: 24 }} />
            </button>
          </Dropdown>
        );
      }
    }
  ];

  const rowSelection = hasApprovePermission
    ? {
        selectedRowKeys: (list ?? []).filter(refund => refund.checked).map(refund => refund.invoice_id),
        onSelect: (refund: IRefund) => onCheck(refund.id),
        onSelectAll: (selected: boolean) => (selected ? onCheckAll() : onUnCheckAll())
      }
    : undefined;

  return (
    <div className='w-full'>
      <Table<IRefund>
        columns={columns}
        dataSource={list ?? []}
        rowKey={refund => refund.invoice_id}
        rowSelection={rowSelection}
        loading={isLoading}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        pagination={{
          current: pagination?.current_page || 1,
          pageSize: pagination?.per_page || 20,
          total: pagination?.total_items || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
          locale: { items_per_page: '/ página' }
        }}
      />
    </div>
  );
};
export default RefundList;

interface IStatusTooltip {
  isExpired: boolean;
  isRequestedAfterDeadline: boolean;
  children: React.ReactNode;
}

const StatusTooltip = (props: IStatusTooltip) => {
  const { isExpired, isRequestedAfterDeadline, children } = props;

  if (!isExpired) return <>{children}</>;

  const tooltipText: string = isRequestedAfterDeadline
    ? 'Após esse prazo o reembolso será cancelado automaticamente'
    : 'Após esse prazo o reembolso será realizado automaticamente';

  return (
    <Tooltip title={tooltipText}>
      <div>{children}</div>
    </Tooltip>
  );
};
